// src/pages/time/TimeOffRequestsPage.jsx
// Extracted from App.js - Time Off Requests view
// BRIO Migration: Using code "3b" (table compact)
import React, { useState } from 'react';
import { Plus, Calendar } from 'lucide-react';
import {
  BrioAnalyticsSurface,
  BrioFrame,
  BrioByCode,
  BrioTable,
  BrioButton,
  BrioIconButton,
  BrioActionGroup,
  BrioSelect,
  getTablePropsFromCode
} from '../../components/BrioComponents';

// Presentation code for this page
const PRESENTATION_CODE = '3b';

// Sentinel value for the "all dates" option in the month selector
const ALL_MONTHS = 'all';

// Build a 'YYYY-MM' key from a 'YYYY-MM-DD' date string (date-only, no TZ shift)
const monthKeyFromDate = (dateStr) => (dateStr ? String(dateStr).slice(0, 7) : null);

// Human-friendly label for a 'YYYY-MM' key, e.g. '2026-06' → 'June 2026'
const monthLabelFromKey = (key) => {
  const [year, month] = key.split('-');
  const d = new Date(Number(year), Number(month) - 1, 1);
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

export default function TimeOffRequestsPage({
  // Data
  currentUser,
  activeUsers,
  activeHolidays,
  timeOff,
  // Supabase
  useSupabaseDB,
  supabaseHolidays,
  supabaseTimeOff,
  // State setters
  setModal,
  setForm,
  setTimeOff,
  setConfirmCancel,
  setEditLeave,
  setConfirmReject,
  // Functions
  isSupervisorOfRequest,
  checkRequestRoutability,
  getWeekIdFromDateStr,
}) {
  const tableProps = getTablePropsFromCode(PRESENTATION_CODE);

  // Local sort state for this page
  const [sortConfig, setSortConfig] = useState({ key: 'startDate', direction: 'desc' });

  // Month-wise filter for the requests list ('all' shows every date)
  const [selectedMonth, setSelectedMonth] = useState(ALL_MONTHS);

  // Today's date as 'YYYY-MM-DD' (used to keep requests visible until end_date passes)
  const todayStr = new Date().toISOString().split('T')[0];
  const isRequestActive = (to) => !to.endDate || to.endDate >= todayStr;

  // Helper to check if a request is unroutable (no team assignment for its week).
  // The live routing check wins: the note stored on the request at submission time
  // goes stale as soon as the missing HFB assignment is filled in.
  const isRequestUnroutable = (request) => {
    if (checkRequestRoutability && request.startDate) {
      const routing = checkRequestRoutability(request.userId, request.startDate);
      return routing.isUnroutable;
    }
    return Boolean(request.reason && request.reason.includes('[System] Unroutable week'));
  };

  // The person who signed the request off, or null while it is still waiting on
  // someone. A supervisor's own request skips the supervisor step rather than
  // being approved by them, so the requester is never shown as their own approver
  // until the request is fully approved. (Older rows were written with the
  // requester's id, so this guards on read as well as on write.)
  const approverFor = (request) => {
    if (request.approvedBy == null) return null;
    if (String(request.approvedBy) === String(request.userId) && request.status !== 'approved') {
      return null;
    }
    return activeUsers.find(x => String(x.id) === String(request.approvedBy)) || null;
  };

  // Emergency requests that pushed the requester past the yearly cap carry a
  // system note. They follow the normal supervisor → admin route; the badge just
  // tells both approvers the cap was already reached.
  const isEmergencyOverCap = (request) => (
    request.type === 'emergency' &&
    typeof request.reason === 'string' &&
    request.reason.includes('Emergency Leave annual cap reached')
  );

  // Extract week ID from unroutable reason or compute it
  const getUnroutableWeekId = (request) => {
    if (request.reason) {
      const match = request.reason.match(/\[System\] Unroutable week ([^:]+):/);
      if (match) return match[1];
    }
    if (getWeekIdFromDateStr && request.startDate) {
      return getWeekIdFromDateStr(request.startDate);
    }
    return null;
  };

  // Filter time off requests based on role
  // Chain: user.teamId → team.supervisorIds → supervisor can see request
  // Requests stay visible (regardless of status) until their end_date has passed.
  const filteredTimeOff = timeOff.filter(to => {
    // Admins see everything
    if (currentUser.role === 'admin' || currentUser.role === 'admin-pa') return true;

    // Supervisors see all requests from their team members.
    // Active requests (end_date >= today) are always shown.
    // Outdated requests are shown if still in any pending state (pending_supervisor,
    // pending, or pending_admin), so the list reflects the full pending pipeline
    // and stays in sync with the badge even for past-dated requests.
    if (currentUser.role === 'supervisor') {
      const isTeamMember = isSupervisorOfRequest(currentUser.id, to);
      const isPending = to.status === 'pending_supervisor' || to.status === 'pending' || to.status === 'pending_admin';
      if (isTeamMember && (isRequestActive(to) || isPending)) {
        return true;
      }
    }

    // Everyone sees their own requests (normalize IDs for comparison)
    if (String(to.userId) === String(currentUser.id)) return true;

    return false;
  });

  // Distinct months present in the visible requests (by start date), newest first.
  // Drives the month-wise dropdown selector.
  const availableMonths = Array.from(
    new Set(filteredTimeOff.map(to => monthKeyFromDate(to.startDate)).filter(Boolean))
  ).sort((a, b) => b.localeCompare(a));

  // Apply the month-wise filter ('all' keeps every request)
  const monthFilteredTimeOff = selectedMonth === ALL_MONTHS
    ? filteredTimeOff
    : filteredTimeOff.filter(to => monthKeyFromDate(to.startDate) === selectedMonth);

  // Priority used when sorting the "actions" column: requests that still need
  // someone to act on them surface first, resolved ones sink to the bottom.
  const actionPriority = (to) => {
    switch (to.status) {
      case 'pending_supervisor':
      case 'pending':
        return 0;
      case 'pending_admin':
        return 1;
      case 'approved':
        return 2;
      case 'rejected':
        return 3;
      default:
        return 4;
    }
  };

  // Sort the filtered time off requests
  const sortedTimeOff = [...monthFilteredTimeOff].sort((a, b) => {
    const userA = activeUsers.find(x => x.id === a.userId);
    const userB = activeUsers.find(x => x.id === b.userId);
    let valA, valB;

    switch(sortConfig.key) {
      case 'employee':
        valA = (userA?.name || '').toLowerCase();
        valB = (userB?.name || '').toLowerCase();
        return sortConfig.direction === 'asc' 
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
          
      case 'type':
        valA = (a.type || '').toLowerCase();
        valB = (b.type || '').toLowerCase();
        return sortConfig.direction === 'asc' 
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
          
      case 'startDate':
        valA = new Date(a.startDate);
        valB = new Date(b.startDate);
        return sortConfig.direction === 'asc' 
          ? valA - valB
          : valB - valA;
          
      case 'workingDays':
        valA = a.workingDays || a.days || 0;
        valB = b.workingDays || b.days || 0;
        return sortConfig.direction === 'asc' 
          ? valA - valB
          : valB - valA;
          
      case 'status':
        valA = (a.status || '').toLowerCase();
        valB = (b.status || '').toLowerCase();
        return sortConfig.direction === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);

      case 'approvedBy': {
        const approverA = approverFor(a);
        const approverB = approverFor(b);
        valA = (approverA?.name || '').toLowerCase();
        valB = (approverB?.name || '').toLowerCase();
        return sortConfig.direction === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }

      case 'actions':
        valA = actionPriority(a);
        valB = actionPriority(b);
        return sortConfig.direction === 'asc'
          ? valA - valB
          : valB - valA;

      default:
        return 0;
    }
  });

  // Helper function to handle sort click
  const handleSortClick = (sortKey) => {
    const newDirection = sortConfig.key === sortKey && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key: sortKey, direction: newDirection });
  };

  // Helper to render sort indicator
  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  return (
    <div className="brio-flex brio-flex-col brio-analytics-font" style={{ background: '#fff' }}>
      {/* PAGE HEADER BAND: Full-width white background, centered content */}
      <div className="brio-pageband brio-pageband--header">
        <BrioFrame>
          <div className="brio-page-header__content">
            <div className="brio-page-header__left">

              <p className="brio-analytics-muted brio-mt-xs">Manage vacation, sick leave, and personal time off</p>
            </div>
            <div className="brio-page-header__right">
              <BrioButton onClick={() => setModal('addTimeOff')}>
                <Plus className="w-4 h-4"/>Request Time Off
              </BrioButton>
            </div>
          </div>
        </BrioFrame>
      </div>

      {/* CONTENT AREA */}
      <BrioFrame>
        <div className="brio-flex brio-flex-col brio-gap-s" style={{ paddingTop: 'var(--brio-space-s)' }}>
          {/* Time Off Requests Section */}
          <BrioAnalyticsSurface padding="m">
            {filteredTimeOff.length > 0 && (
              <div className="brio-flex brio-justify-between brio-items-center brio-mb-s brio-flex-wrap brio-gap-xs">
                <div>
                  <h3 className="brio-analytics-section-title">time off requests</h3>
                  <p className="brio-analytics-muted brio-mt-xs">
                    {selectedMonth === ALL_MONTHS
                      ? `Showing all dates · ${filteredTimeOff.length} request${filteredTimeOff.length === 1 ? '' : 's'}`
                      : `Showing ${monthLabelFromKey(selectedMonth)} · ${monthFilteredTimeOff.length} request${monthFilteredTimeOff.length === 1 ? '' : 's'}`}
                  </p>
                </div>
                <div className="brio-flex brio-items-center brio-gap-xs">
                  <label htmlFor="timeoff-month-filter" className="brio-analytics-muted">Month</label>
                  <BrioSelect
                    id="timeoff-month-filter"
                    value={selectedMonth}
                    onChange={e => setSelectedMonth(e.target.value)}
                  >
                    <option value={ALL_MONTHS}>All dates</option>
                    {availableMonths.map(key => (
                      <option key={key} value={key}>{monthLabelFromKey(key)}</option>
                    ))}
                  </BrioSelect>
                </div>
              </div>
            )}
            {monthFilteredTimeOff.length > 0 ? (
              <BrioByCode code={PRESENTATION_CODE}>
                <BrioTable {...tableProps}>
                  <thead>
                    <tr>
                      {(currentUser.role === 'admin' || currentUser.role === 'admin-pa' || currentUser.role === 'supervisor') && (
                        <th 
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSortClick('employee')}
                        >
                          employee{getSortIndicator('employee')}
                        </th>
                      )}
                      <th 
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleSortClick('type')}
                      >
                        type{getSortIndicator('type')}
                      </th>
                      <th 
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleSortClick('startDate')}
                      >
                        dates{getSortIndicator('startDate')}
                      </th>
                      <th 
                        className="text-center"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleSortClick('workingDays')}
                      >
                        working days{getSortIndicator('workingDays')}
                      </th>
                      <th
                        className="text-center"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleSortClick('status')}
                      >
                        status{getSortIndicator('status')}
                      </th>
                      <th
                        className="text-center"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleSortClick('approvedBy')}
                      >
                        approved by{getSortIndicator('approvedBy')}
                      </th>
                      <th
                        className="text-center col-actions"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleSortClick('actions')}
                      >
                        actions{getSortIndicator('actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTimeOff.map(to => {
                      const u = activeUsers.find(x => x.id === to.userId);
                      const approver = approverFor(to);
                      const supervisorAlreadyApproved = (
                        to.status === 'pending_admin' ||
                        to.status === 'approved'
                      ) && to.supervisorApproval === 'approved';
                      const canDenyOtherSupervisor = (
                        currentUser.role === 'supervisor' &&
                        supervisorAlreadyApproved &&
                        isSupervisorOfRequest(currentUser.id, to) &&
                        String(to.userId) !== String(currentUser.id) &&
                        String(to.approvedBy) !== String(currentUser.id) &&
                        isRequestActive(to)
                      );
                      return (
                        <tr key={to.id} className="brio-row--hoverable">
                          {(currentUser.role === 'admin' || currentUser.role === 'admin-pa' || currentUser.role === 'supervisor') && (
                            <td>
                              <span className="brio-font-medium">{u?.name}</span>
                            </td>
                          )}
                          <td>
                            <span className="brio-font-medium" style={{ textTransform: 'capitalize' }}>{to.type}</span>
                          </td>
                          <td>
                            <span className="brio-text-grey">{to.startDate} to {to.endDate}</span>
                          </td>
                          <td className="text-center">
                            <span className="brio-font-medium" style={{ color: 'var(--brio-gold)' }}>
                              {to.workingDays || to.days} {(to.workingDays || to.days) === 1 ? 'day' : 'days'}
                            </span>
                          </td>
                          <td className="text-center">
                            <div className="brio-flex brio-flex-col brio-items-center brio-gap-xs">
                              <span className={`brio-badge ${
                                to.status === 'approved' ? 'brio-badge--approved' :
                                to.status === 'pending_supervisor' ? 'brio-badge--warning' :
                                to.status === 'pending' ? 'brio-badge--warning' :
                                to.status === 'pending_admin' ? 'brio-badge--pending' :
                                to.status === 'rejected' ? 'brio-badge--danger' :
                                'brio-badge--pending'
                              }`}>
                                {to.status === 'pending_supervisor' && (currentUser.role === 'admin' || currentUser.role === 'admin-pa' || currentUser.role === 'supervisor') ? 'Awaiting Supervisor' :
                                 to.status === 'pending' && (currentUser.role === 'admin' || currentUser.role === 'admin-pa' || currentUser.role === 'supervisor') ? 'Awaiting Supervisor' :
                                 to.status === 'pending_admin' && (currentUser.role === 'admin' || currentUser.role === 'admin-pa' || currentUser.role === 'supervisor') ? 'Awaiting Admin' :
                                 to.status === 'pending_supervisor' || to.status === 'pending_admin' || to.status === 'pending' ? 'Pending Approval' :
                                 to.status === 'approved' ? 'Approved' :
                                 to.status === 'rejected' ? 'Rejected' : to.status}
                              </span>
                              {/* ANTI-REGRESSION GUARDRAIL: Show unroutable badge */}
                              {isRequestUnroutable(to) && (to.status === 'pending_supervisor' || to.status === 'pending') && (
                                <span
                                  className="brio-badge brio-badge--warning"
                                  title={(currentUser.role === 'admin' || currentUser.role === 'admin-pa')
                                    ? `Assign this user to a team in HFB for week ${getUnroutableWeekId(to)} so a supervisor can review it.`
                                    : 'This request is pending team assignment'}
                                >
                                  Unassigned week
                                </span>
                              )}
                              {isEmergencyOverCap(to) && (to.status === 'pending_supervisor' || to.status === 'pending' || to.status === 'pending_admin') && (
                                <span
                                  className="brio-badge brio-badge--warning"
                                  title="Emergency Leave for this year is already at the cap. Supervisor approval is still required, then admin sign-off."
                                >
                                  Over emergency cap
                                </span>
                              )}
                              {/* Show helper text for users on their own unroutable requests */}
                              {isRequestUnroutable(to) && String(to.userId) === String(currentUser.id) && (to.status === 'pending_supervisor' || to.status === 'pending') && currentUser.role === 'user' && (
                                <span style={{ fontSize: '9px', color: 'var(--brio-orange)', marginTop: '2px' }}>
                                  (team assignment required)
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="text-center">
                            {approver ? (
                              <span className="brio-font-medium">{approver.name}</span>
                            ) : (
                              <span className="brio-text-grey">—</span>
                            )}
                          </td>
                          <td className="text-center">
                            <BrioActionGroup center>
                              {/* Admin/admin-pa can edit or cancel any leave regardless of status */}
                              {(currentUser.role === 'admin' || currentUser.role === 'admin-pa') && (
                                <>
                                  <BrioIconButton
                                    icon="delete"
                                    variant="delete"
                                    title="Cancel this leave"
                                    onClick={() => {
                                      setConfirmCancel({
                                        leaveId: to.id,
                                        startDate: to.startDate,
                                        endDate: to.endDate,
                                        type: to.type,
                                        workingDays: to.workingDays || to.days
                                      });
                                    }}
                                  />
                                  <BrioIconButton
                                    icon="edit"
                                    variant="edit"
                                    title="Edit this leave"
                                    onClick={() => {
                                      setEditLeave({
                                        ...to,
                                        originalStartDate: to.startDate,
                                        originalEndDate: to.endDate,
                                        originalType: to.type
                                      });
                                    }}
                                  />
                                </>
                              )}

                              {/* Own pending requests – edit or withdraw before approval (non-admin only; approved leaves require admin/supervisor to amend) */}
                              {String(to.userId) === String(currentUser.id) && (to.status === 'pending_supervisor' || to.status === 'pending_admin' || to.status === 'pending') && (currentUser.role !== 'admin' && currentUser.role !== 'admin-pa') && (
                                <>
                                  <BrioIconButton
                                    icon="edit"
                                    variant="edit"
                                    title="Edit this request"
                                    onClick={() => {
                                      setEditLeave({
                                        ...to,
                                        originalStartDate: to.startDate,
                                        originalEndDate: to.endDate,
                                        originalType: to.type
                                      });
                                    }}
                                  />
                                  <BrioIconButton
                                    icon="delete"
                                    variant="delete"
                                    title="Withdraw this request"
                                    onClick={async () => {
                                      if (window.confirm('Withdraw this time off request? This cannot be undone.')) {
                                        if (useSupabaseDB) {
                                          const result = await supabaseTimeOff.deleteTimeOffRequest(to.id);
                                          if (!result.success) {
                                            alert('Error withdrawing request: ' + result.error);
                                          }
                                        } else {
                                          setTimeOff(timeOff.filter(t => t.id !== to.id));
                                        }
                                      }
                                    }}
                                  />
                                </>
                              )}

                              {/* Supervisor approval buttons */}
                              {currentUser.role === 'supervisor' && (to.status === 'pending_supervisor' || to.status === 'pending') && isSupervisorOfRequest(currentUser.id, to) && (
                                <>
                                  <BrioButton
                                    variant="success"
                                    className="brio-btn--sm"
                                    title="Approve as supervisor"
                                    type="button"
                                    onClick={async () => {
                                      const updateData = {
                                        supervisorApproval: 'approved',
                                        status: 'pending_admin',
                                        approvedBy: currentUser.id,
                                        approvedAt: new Date().toISOString()
                                      };

                                      if (useSupabaseDB) {
                                        const result = await supabaseTimeOff.updateTimeOffRequest(to.id, updateData);
                                        if (!result.success) {
                                          alert('Error approving request: ' + result.error);
                                          return;
                                        }
                                      } else {
                                        setTimeOff(timeOff.map(t => t.id === to.id ? { ...t, ...updateData } : t));
                                      }
                                      alert('✓ Approved! Request forwarded to admin.');
                                    }}
                                  >
                                    ✓ Approve
                                  </BrioButton>
                                  <BrioButton
                                    variant="danger"
                                    className="brio-btn--sm"
                                    title="Reject as supervisor"
                                    type="button"
                                    onClick={() => {
                                      setConfirmReject({
                                        requestId: to.id,
                                        type: 'supervisor',
                                        userName: u?.name
                                      });
                                    }}
                                  >
                                    ✗ Reject
                                  </BrioButton>
                                </>
                              )}

                              {/* Other supervisors can override a prior supervisor's approval
                                  (status is pending_admin or approved) until the leave's end_date. */}
                              {canDenyOtherSupervisor && (
                                <BrioButton
                                  variant="danger"
                                  className="brio-btn--sm"
                                  title="Deny this approval"
                                  type="button"
                                  onClick={() => {
                                    setConfirmReject({
                                      requestId: to.id,
                                      type: 'supervisor',
                                      userName: u?.name,
                                      overrideApproval: true,
                                      approverName: approver?.name || null
                                    });
                                  }}
                                >
                                  ✗ Deny Approval
                                </BrioButton>
                              )}
                              
                              {/* Admin approval buttons */}
                              {(currentUser.role === 'admin' || currentUser.role === 'admin-pa') && to.status === 'pending_admin' && (
                                <>
                                  <BrioButton
                                    variant="success"
                                    className="brio-btn--sm"
                                    title="Approve as admin"
                                    type="button"
                                    onClick={async () => {
                                      const updateData = {
                                        adminApproval: 'approved',
                                        status: 'approved',
                                        approvedBy: currentUser.id,
                                        approvedAt: new Date().toISOString()
                                      };

                                      if (useSupabaseDB) {
                                        const result = await supabaseTimeOff.updateTimeOffRequest(to.id, updateData);
                                        if (!result.success) {
                                          alert('Error approving request: ' + result.error);
                                          return;
                                        }
                                      } else {
                                        setTimeOff(timeOff.map(t => t.id === to.id ? { ...t, ...updateData } : t));
                                      }
                                      alert('✓ Leave request approved!');
                                    }}
                                  >
                                    ✓ Approve
                                  </BrioButton>
                                  <BrioButton
                                    variant="danger"
                                    className="brio-btn--sm"
                                    title="Reject as admin"
                                    type="button"
                                    onClick={() => {
                                      setConfirmReject({
                                        requestId: to.id,
                                        type: 'admin',
                                        userName: u?.name
                                      });
                                    }}
                                  >
                                    ✗ Reject
                                  </BrioButton>
                                </>
                              )}
                            </BrioActionGroup>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </BrioTable>
              </BrioByCode>
            ) : (
              <div className="brio-text-center brio-analytics-muted" style={{ padding: '48px 0' }}>
                <Calendar className="w-12 h-12 mx-auto brio-mb-s" style={{ color: 'var(--brio-border)' }}/>
                <p>
                  {filteredTimeOff.length > 0 && selectedMonth !== ALL_MONTHS
                    ? `No time off requests in ${monthLabelFromKey(selectedMonth)}`
                    : 'No time off requests yet'}
                </p>
              </div>
            )}
          </BrioAnalyticsSurface>

          {/* Company Holidays Section */}
          <BrioAnalyticsSurface padding="m">
            <div className="brio-flex brio-justify-between brio-items-center brio-mb-s">
              <div>
                <h3 className="brio-analytics-section-title">company holidays</h3>
                <p className="brio-analytics-muted brio-mt-xs">Organization-wide holidays and observances</p>
              </div>
              {(currentUser.role === 'admin' || currentUser.role === 'admin-pa') && (
                <BrioButton onClick={() => setModal('addHoliday')}>
                  <Plus className="w-4 h-4"/>Add Holiday
                </BrioButton>
              )}
            </div>
            
            {activeHolidays.length > 0 ? (
              <BrioByCode code={PRESENTATION_CODE}>
                <BrioTable {...tableProps}>
                  <thead>
                    <tr>
                      <th>holiday</th>
                      <th>date</th>
                      <th>office</th>
                      {(currentUser.role === 'admin' || currentUser.role === 'admin-pa') && <th className="text-center col-actions">actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {activeHolidays.map(h => (
                      <tr key={h.id} className="brio-row--hoverable">
                        <td>
                          <span className="brio-font-medium">{h.name}</span>
                        </td>
                        <td>
                          <span className="brio-text-grey">{new Date(h.date).toLocaleDateString('en-IN', {weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'})}</span>
                        </td>
                        <td>
                          <span className={`brio-badge ${h.office === 'NL' ? 'brio-badge--info' : h.office === 'IN' ? 'brio-badge--warning' : 'brio-badge--neutral'}`}>
                            {h.office === 'NL' ? 'NL' : h.office === 'IN' ? 'IN' : 'All'}
                          </span>
                        </td>
                        {(currentUser.role === 'admin' || currentUser.role === 'admin-pa') && (
                          <td className="text-center">
                            <div className="brio-flex brio-justify-center brio-gap-xs">
                              <BrioIconButton
                                icon="edit"
                                variant="edit"
                                title="Edit holiday"
                                onClick={() => {
                                  setForm({ holidayId: h.id, holidayName: h.name, holidayDate: h.date, holidayOffice: h.office || '' });
                                  setModal('editHoliday');
                                }}
                              />
                              <BrioIconButton
                                icon="delete"
                                variant="delete"
                                title="Delete holiday"
                                onClick={async () => {
                                  if(window.confirm(`Delete holiday "${h.name}"?`)) {
                                    const result = await supabaseHolidays.deleteHoliday(h.id);
                                    if (!result.success) {
                                      alert('Error deleting holiday: ' + result.error);
                                    }
                                  }
                                }}
                              />
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </BrioTable>
              </BrioByCode>
            ) : (
              <div className="brio-text-center brio-analytics-muted" style={{ padding: '48px 0' }}>
                <Calendar className="w-12 h-12 mx-auto brio-mb-s" style={{ color: 'rgba(0, 0, 0, 0.2)' }}/>
                <p>No holidays added yet</p>
              </div>
            )}
          </BrioAnalyticsSurface>

          {(currentUser.role === 'admin' || currentUser.role === 'admin-pa') && (
            <div className="brio-border brio-analytics-font" style={{ background: 'var(--brio-gold-light)', borderColor: 'var(--brio-gold)', padding: '16px' }}>
              <h3 className="brio-analytics-body brio-font-medium brio-mb-xs" style={{ color: 'var(--brio-gold-hover)' }}>📋 Admin Features</h3>
              <ul className="brio-analytics-muted" style={{ color: 'var(--brio-gold-hover)' }}>
                <li>• Approve or reject pending time off requests</li>
                <li>• Add and manage company-wide holidays</li>
                <li>• View all team members' time off requests</li>
                <li>• Holidays are automatically shown in the Calendar view</li>
              </ul>
            </div>
          )}
        </div>
      </BrioFrame>
    </div>
  );
}
